import React, { forwardRef } from 'react';
import styles from './PrescriptionReportTemplate.module.css';
import Header from './header/Header';
import PersonInfos from './person-infos/PersonInfos';
import Signature from './signature/Signature';
import RemotePath from '../../consts/RemotePaths';
import { companyHeaderProps, professionalInfos } from './report/reportCompany';
import {
  copyLabel,
  formatIssuedAt,
  isSpecial,
  patientAddress,
} from '../../pages/appointment/modules/prescription/prescriptionModel';

const BUYER_FIELDS = [
  ['Nome', 'wide'],
  ['Identidade', ''],
  ['Órgão emissor', ''],
  ['Endereço', 'wide'],
  ['Cidade', ''],
  ['UF', 'short'],
  ['Telefone', ''],
];

const signatureUrl = (company, professional) => (
  professional && professional.Signature && company.Id !== undefined
    ? RemotePath.PATH_USER_UPLOADS_COMPANY_RESOURCE_SIGNATURE(company.Id) + professional.Signature
    : ''
);

/** Quadro do emitente da receita de controle especial: profissional e endereço da clínica. */
const IssuerBox = ({ company, professional }) => {
  const header = companyHeaderProps(company);
  const [street, city] = header.address.split('\n');
  const rows = [
    ['Nome', professional ? professional.Label : ''],
    ['Registro', professional ? professional.Regulator : ''],
    ['Endereço', street || ''],
    ['Cidade e UF', city || ''],
    ['Telefone', header.phone],
  ];
  return (
    <div className={styles.box}>
      <span className={styles.boxTitle}>Identificação do emitente</span>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.boxRow}>
          <span className={styles.boxLabel}>{label}:</span>
          <span className={styles.boxValue}>{value || ''}</span>
        </div>
      ))}
    </div>
  );
};

/** Campos em branco do comprador e do fornecedor, preenchidos à mão na farmácia. */
const PharmacyBoxes = () => (
  <div className={styles.pharmacy}>
    <div className={styles.box}>
      <span className={styles.boxTitle}>Identificação do comprador</span>
      <div className={styles.blankGrid}>
        {BUYER_FIELDS.map(([label, size]) => (
          <div key={label} className={`${styles.blankField} ${size ? styles[size] : ''}`}>
            <span className={styles.boxLabel}>{label}</span>
            <span className={styles.blankLine} />
          </div>
        ))}
      </div>
    </div>
    <div className={styles.box}>
      <span className={styles.boxTitle}>Identificação do fornecedor</span>
      <div className={styles.blankGrid}>
        <div className={`${styles.blankField} ${styles.wide}`}>
          <span className={styles.boxLabel}>Assinatura do farmacêutico</span>
          <span className={styles.blankLine} />
        </div>
        <div className={`${styles.blankField} ${styles.wide}`}>
          <span className={styles.boxLabel}>Data</span>
          <span className={styles.blankLine} />
        </div>
      </div>
    </div>
  </div>
);

/** Uma via da receita, numa folha A4. */
const PrescriptionCopy = ({ prescription, company, professional, patient, index, total }) => {
  const special = isSpecial(prescription);
  const label = copyLabel(prescription.kind, index, total);
  const date = formatIssuedAt(prescription.issuedAt);
  const address = patientAddress(patient);
  const sign = Number(prescription.signed) === 1 ? signatureUrl(company, professional) : '';

  return (
    <div className={`${styles.page} ${index > 0 ? styles.pageBreak : ''}`}>
      {company.Name && <Header {...companyHeaderProps(company)} />}

      <div className={styles.titleRow}>
        <h2 className={styles.title}>{special ? 'Receituário de controle especial' : 'Receituário'}</h2>
        {label && <span className={styles.copyLabel}>{label}</span>}
      </div>

      {special ? (
        <div className={styles.people}>
          <IssuerBox company={company} professional={professional} />
          <div className={styles.box}>
            <span className={styles.boxTitle}>Paciente</span>
            <div className={styles.boxRow}>
              <span className={styles.boxLabel}>Nome:</span>
              <span className={styles.boxValue}>{patient.name || ''}</span>
            </div>
            <div className={styles.boxRow}>
              <span className={styles.boxLabel}>Endereço:</span>
              <span className={styles.boxValue}>{address}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.peopleInline}>
          <PersonInfos data={[{ key: 'Paciente:', value: patient.name || '' }, ...(address ? [{ key: 'Endereço:', value: address }] : [])]} />
          {professional && <PersonInfos data={professionalInfos(professional)} />}
        </div>
      )}

      <div className={styles.prescription}>
        <span className={styles.prescriptionLabel}>Prescrição</span>
        <div className={styles.prescriptionText}>{prescription.content}</div>
      </div>

      <div className={styles.signature}>
        <Signature date={date} name={professional ? professional.Label || '' : ''} sign={sign} />
      </div>

      {special && <PharmacyBoxes />}
    </div>
  );
};

/**
 * PDF da receita no papel da clínica: uma folha por via (`copies`). A de
 * controle especial segue o leiaute da Portaria SVS/MS 344/98: emitente,
 * paciente, prescrição, data e assinatura, e os campos do comprador e do
 * fornecedor em branco. A assinatura digitalizada do profissional só entra
 * quando a receita foi marcada como assinada.
 *
 * `patient`: cadastro do paciente (nome e endereço);
 * `professional`: recurso da clínica (Label, Regulator, Signature) ou null.
 */
const PrescriptionReportTemplate = forwardRef(({ prescription, companyData, professional, patient }, ref) => {
  if (!prescription) return null;
  const company = companyData || {};
  const total = Math.max(1, parseInt(prescription.copies, 10) || 1);

  return (
    <div ref={ref} className={styles.document}>
      {Array.from({ length: total }).map((_, index) => (
        <PrescriptionCopy
          key={index}
          prescription={prescription}
          company={company}
          professional={professional}
          patient={patient || {}}
          index={index}
          total={total}
        />
      ))}
    </div>
  );
});

PrescriptionReportTemplate.displayName = 'PrescriptionReportTemplate';

export default PrescriptionReportTemplate;
